import { logError } from '@/utils/errorUtils'
import { Loader, Menu, SegmentedControl, Tooltip } from '@mantine/core'
import {
  IconCalendarWeek,
  IconLayoutList,
  IconListTree,
  IconSortAscending,
  IconSortDescending,
} from '@tabler/icons-react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { AuditEvent, Patient } from '@medplum/fhirtypes'
import { useEffect, useState } from 'react'
import { useFhirSearch, useDateRangeFilter } from '@/hooks'
import type { AvailResource } from '@/hooks/fhirSearchConfig.ts'
import { showNotification } from '@mantine/notifications'
import { useMedplum } from '@medplum/react'
import { DatePicker } from '@mantine/dates'
import { formatDateTime, formatPatientName } from '@/utils.ts'
import { extractTitleAndIcon, isAiReportDocument } from './ChronologicalRecords.utils.ts'
import { isDoseSpotPrescription } from './doseSpotPrescription.ts'
import dayjs from 'dayjs'
import { LabResults } from './LabResults.tsx'
import { Documents } from './Documents.tsx'
import { Orders } from './Orders.tsx'
import { Insurances } from './Insurances.tsx'
import * as CRC from './ChronologicalRecordComponents'
import { ConsentDocuments } from './ConsentDocuments.tsx'
import { DocumentReference } from '@medplum/fhirtypes'
import { GroupedTimelineView } from './GroupedTimelineView'
import { CHART_CONTENT_TOP_VAR } from '@/pages/Patient/chartTopOffset'

type ViewMode = 'grouped' | 'flat'

type Props = {
  patient: Patient
}

export function ChronologicalRecords({ patient }: Props) {
  const [resources, setResources] = useState<AuditEvent[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [sorting, setSorting] = useState<'desc' | 'asc'>('desc')
  const [selectedResource, setSelectedResource] = useState<AuditEvent>()
  const [viewMode, setViewMode] = useState<ViewMode>('grouped')
  const [isLoading, setIsLoading] = useState(false)
  // Same anchor as `<ListLayout>`: bypass ScrollContext (CallPage publishes
  // `stickyTop="0px"`, wrong for a viewport-`fixed` rail that must clear the
  // Shell header + PatientChartActionBar) and use the shared CSS expression.
  const stickyTop = CHART_CONTENT_TOP_VAR

  const medplum = useMedplum()
  const { search, getSearchQuery, loadMore, setSort, setFilters, removeFilter } = useFhirSearch(
    'AuditEvent',
    50,
    'date',
  )
  const { dateRange, onDateRange } = useDateRangeFilter(search, setFilters, removeFilter)

  function renderDocument(resource: DocumentReference) {
    if (resource.type?.coding?.find((item) => item.code === '64290-0')) {
      return (
        <ConsentDocuments
          title={sourceItem?.title || 'Consent Document'}
          sourceResourceId={neededResource?.id}
        />
      )
    }
    return (
      <Documents title={sourceItem?.title || 'Document'} sourceResourceId={neededResource?.id} />
    )
  }
  useEffect(() => {
    const searchQuery = getSearchQuery()
    if (medplum && searchQuery) {
      setIsLoading(true)
      const resourceType = 'AuditEvent'
      const searchParams = `agent=Patient/${patient.id}&_include=${resourceType}:entity&type=timeline`
      medplum
        .search(resourceType, `${searchQuery}&${searchParams}`)
        .then((result) => {
          let resourceList: AuditEvent[] = []
          for (const entry of result.entry || []) {
            const resource = entry.resource as AvailResource | undefined
            if (!resource) {
              continue
            }

            if (resource.resourceType === 'AuditEvent') {
              const auditEvent = resource
              if (auditEvent.entity?.[0].what?.reference) {
                const [type, id] = auditEvent.entity[0].what.reference.split('/')
                const entity = result.entry?.find(
                  (item) => item.resource?.resourceType === type && item.resource?.id === id,
                )

                if (entity) {
                  auditEvent.entity[0].what.resource = entity.resource
                }
              }

              const linkedResource = auditEvent.entity?.[0]?.what?.resource as
                | { resourceType?: string; status?: string }
                | undefined
              const isRemovedChartRecord =
                (linkedResource?.resourceType === 'DocumentReference' ||
                  linkedResource?.resourceType === 'DiagnosticReport') &&
                linkedResource.status === 'entered-in-error'
              if (
                isRemovedChartRecord ||
                isAiReportDocument(auditEvent.entity?.[0]?.what?.resource)
              ) {
                continue
              }

              resourceList.push(auditEvent)
            }
          }

          if (search.offset !== 0) {
            resourceList = [...resources, ...resourceList]
          }

          setResources(resourceList)

          setHasMore(!!result.link?.find((item) => item.relation === 'next'))
          setIsLoading(false)
        })
        .catch((e) => {
          logError('patient', 'Failed to fetch chronological records', e)
          setIsLoading(false)
          showNotification({
            title: 'Error',
            message: 'Failed to fetch resources',
            color: 'red',
          })
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medplum, search, sorting])

  const onSorting = () => {
    const newSort = sorting === 'desc' ? 'asc' : 'desc'
    setSort('date', newSort)
    setSorting(newSort)
  }

  const onResourceSelect = (resource: AuditEvent) => {
    setSelectedResource(resource)
  }

  const today = dayjs()

  const neededResource = selectedResource?.entity?.[0].what?.resource
  const { item: sourceItem } = extractTitleAndIcon(neededResource)

  /*
        'Encounter'
   */

  return (
    <div className="flex w-full items-start gap-2">
      <div
        id="scrollableDiv"
        className="fixed w-[280px] overflow-auto bg-[#FBFCFE] pt-4"
        style={{ top: stickyTop, height: `calc(100vh - ${stickyTop})` }}
      >
        {/* View Mode Toggle */}
        <div className="px-4 pb-2">
          <SegmentedControl
            size="xs"
            value={viewMode}
            onChange={(value) => setViewMode(value as ViewMode)}
            data={[
              {
                value: 'grouped',
                label: (
                  <Tooltip label="Grouped by date">
                    <div className="flex items-center gap-1">
                      <IconListTree size={14} />
                      <span>Grouped</span>
                    </div>
                  </Tooltip>
                ),
              },
              {
                value: 'flat',
                label: (
                  <Tooltip label="Flat list">
                    <div className="flex items-center gap-1">
                      <IconLayoutList size={14} />
                      <span>Flat</span>
                    </div>
                  </Tooltip>
                ),
              },
            ]}
            fullWidth
          />
        </div>

        <div className="flex items-center justify-between px-4 pb-4 text-xs text-[#7D7987]">
          <div
            className="flex cursor-pointer items-center gap-1 hover:opacity-70"
            onClick={onSorting}
          >
            {sorting === 'desc' ? (
              <>
                <IconSortDescending size={15} />
                Newest first
              </>
            ) : (
              <>
                <IconSortAscending size={15} />
                Oldest first
              </>
            )}
          </div>
          <div>
            <Menu shadow="md">
              <Menu.Target>
                <div className="flex cursor-pointer items-center gap-1 hover:opacity-70">
                  <IconCalendarWeek size={15} /> Select dates
                </div>
              </Menu.Target>

              <Menu.Dropdown>
                <DatePicker
                  type="range"
                  value={dateRange}
                  // PD-1901 — reopen on the chosen range, not today.
                  defaultDate={dateRange?.[0] ?? undefined}
                  presets={[
                    {
                      value: [null, null],
                      label: 'Reset',
                    },
                    {
                      value: [
                        today.subtract(1, 'day').format('YYYY-MM-DD'),
                        today.format('YYYY-MM-DD'),
                      ],
                      label: 'Last two days',
                    },
                    {
                      value: [
                        today.subtract(6, 'day').format('YYYY-MM-DD'),
                        today.format('YYYY-MM-DD'),
                      ],
                      label: 'Last 7 days',
                    },
                    {
                      value: [
                        today.startOf('month').format('YYYY-MM-DD'),
                        today.format('YYYY-MM-DD'),
                      ],
                      label: 'This month',
                    },
                    {
                      value: [
                        today.subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
                        today.subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
                      ],
                      label: 'Last month',
                    },
                    {
                      value: [
                        today.subtract(1, 'year').startOf('year').format('YYYY-MM-DD'),
                        today.subtract(1, 'year').endOf('year').format('YYYY-MM-DD'),
                      ],
                      label: 'Last year',
                    },
                  ]}
                  onChange={onDateRange}
                  className="w-full"
                />
              </Menu.Dropdown>
            </Menu>
          </div>
        </div>

        {/* Grouped View */}
        {viewMode === 'grouped' && (
          <GroupedTimelineView
            resources={resources}
            selectedResource={selectedResource}
            onResourceSelect={onResourceSelect}
            hasMore={hasMore}
            onLoadMore={loadMore}
            sorting={sorting}
            patientId={patient.id || ''}
            patientName={formatPatientName(patient.name?.[0]) || 'Unknown'}
            patientInfo={`(${patient.gender?.charAt(0)?.toUpperCase() || '?'}) ${patient.birthDate || ''}`}
            isLoading={isLoading}
          />
        )}

        {/* Flat View (original) */}
        {viewMode === 'flat' && (
          <>
            {!resources.length && (
              <div className="px-4 text-center text-sm">
                {isLoading ? 'Loading records...' : 'No records available'}
              </div>
            )}

            <InfiniteScroll
              dataLength={resources.length}
              next={loadMore}
              hasMore={hasMore}
              loader={<Loader color="blue" />}
              scrollableTarget="scrollableDiv"
              style={{ display: 'contents' }}
            >
              {resources.map((resource) => {
                const {
                  title,
                  icon: Icon,
                  color,
                  item,
                } = extractTitleAndIcon(resource.entity?.[0].what?.resource)
                if (!item) {
                  return null
                }
                return (
                  <div
                    key={resource.id}
                    onClick={() => onResourceSelect(resource)}
                    className={[
                      'cursor-pointer border-b-1 border-b-[#D0E1F2] px-4 py-2 text-[13px]',
                      resource.id === selectedResource?.id ? 'bg-[#dcebfd]' : 'hover:bg-[#F0F7FF]',
                    ].join(' ')}
                  >
                    <div className="flex justify-between">
                      <p
                        className="text-primary flex-1 break-words"
                        dangerouslySetInnerHTML={{
                          __html: title.replace('(Plain text)', ''),
                        }}
                      />
                      <p>
                        <Icon size={15} color={color} />
                      </p>
                    </div>
                    <p className="text-[#7D7987]">{formatDateTime(resource.recorded, '-', true)}</p>
                  </div>
                )
              })}
            </InfiniteScroll>
          </>
        )}
      </div>

      <div className="ml-[280px] flex-1 p-4" style={{ width: 'calc(100% - 280px)' }}>
        <p className="mb-4 border-b-1 border-b-gray-200 pb-2 text-2xl font-bold">
          Chronological Record
        </p>
        {neededResource?.resourceType === 'DocumentReference' && renderDocument(neededResource)}

        {neededResource?.resourceType === 'RequestGroup' && (
          <Orders sourceResourceId={neededResource?.id} />
        )}
        {neededResource?.resourceType === 'DiagnosticReport' && (
          <LabResults sourceResourceId={neededResource?.id} />
        )}
        {neededResource?.resourceType === 'Coverage' && <Insurances patient={patient} />}
        {sourceItem?.dataKey === 'diagnosis' && <CRC.Diagnoses resource={neededResource} />}
        {sourceItem?.dataKey === 'vitals' && <CRC.Vitals resource={neededResource} />}
        {/*
         * Both kinds of medication row land under the same chart section, so the
         * resource picks the renderer, not the section. The test is the same one the
         * rail label uses — a DoseSpot-authored row, not merely a `MedicationRequest`
         * — so nothing that DoseSpot did not write is ever labelled as though it did.
         */}
        {sourceItem?.dataKey === 'medications' &&
          (neededResource && isDoseSpotPrescription(neededResource) ? (
            <CRC.DoseSpotPrescription prescription={neededResource} />
          ) : (
            <CRC.Medications resource={neededResource} />
          ))}
        {sourceItem?.dataKey === 'allergies' && <CRC.Allergies resource={neededResource} />}
        {sourceItem?.dataKey === 'immunizations' && <CRC.Immunizations resource={neededResource} />}
        {sourceItem?.dataKey === 'procedures' && <CRC.Procedures resource={neededResource} />}
        {sourceItem?.dataKey === 'encounters' && <CRC.Encounters resource={neededResource} />}
        {sourceItem?.dataKey === 'family-history' && (
          <CRC.FamilyHistory resource={neededResource} />
        )}
        {sourceItem?.dataKey === 'social-history' && (
          <CRC.SocialHistory resource={neededResource} />
        )}
      </div>
    </div>
  )
}
